// components/VesselTools.js
// Vessel Sizing (ASME Sec VIII Div 1) + Wall Thickness Calculator
import React, { useState } from 'react';
import {
  allowableStressValues, materialInfo, materialDensities, materialCostFactors,
  getInterpolatedStress, getStandardThickness, recommendMaterial,
} from './vessel-sizing/MaterialData';

// ─── Wall Thickness Calculator ──────────────────────────────────────────────

function WallThicknessCalculator() {
  const [diameter,           setDiameter]           = useState(1.0);   // m
  const [designPressure,     setDesignPressure]     = useState(100);   // barg
  const [designTemperature,  setDesignTemperature]  = useState(80);    // °C
  const [selectedMaterial,   setSelectedMaterial]   = useState('SA-516-70');
  const [jointEfficiency,    setJointEfficiency]    = useState(0.85);
  const [corrosionAllowance, setCorrosionAllowance] = useState(3.0);   // mm
  const [headType,           setHeadType]           = useState('elliptical');

  const S  = getInterpolatedStress(selectedMaterial, designTemperature);
  const P  = designPressure / 10;        // barg → MPa
  const R  = (diameter * 1000) / 2;     // mm inside radius
  const E  = jointEfficiency;
  const CA = corrosionAllowance;

  // ASME VIII Div 1 UG-27 — cylindrical shell
  const tShell = (P * R) / (S * E - 0.6 * P) + CA;

  // Head thickness (varies by type)
  let tHead;
  if (headType === 'hemispherical') {
    tHead = (P * R) / (2 * S * E - 0.2 * P) + CA;
  } else if (headType === 'elliptical') {
    // 2:1 semi-elliptical: t = PD/(2SE-0.2P)
    tHead = (P * diameter * 1000) / (2 * S * E - 0.2 * P) + CA;
  } else {
    // Torispherical: L = OD ≈ D+2t, knuckle r=6%D, M factor
    const L = diameter * 1000;
    const r_k = 0.06 * L;
    const Mratio = L / r_k;
    const M = 0.25 * (3 + Math.sqrt(Mratio));
    tHead = (P * L * M) / (2 * S * E - 0.2 * P) + CA;
  }

  const stdShell = getStandardThickness(tShell);
  const stdHead  = getStandardThickness(tHead);

  const matName = materialInfo[selectedMaterial]?.name || selectedMaterial;

  return (
    <div className="tool-panel">
      <h3>ASME Section VIII Div. 1 — Wall Thickness Calculator</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Cylindrical shells and heads under internal pressure per UG-27
      </p>

      <div className="two-col-grid">
        <div className="input-group">
          <label>Inside Diameter (m)</label>
          <input type="number" step="0.1" min="0.1" max="10"
            value={diameter} onChange={e=>setDiameter(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Design Pressure (barg)</label>
          <input type="number" step="1" min="1" max="500"
            value={designPressure} onChange={e=>setDesignPressure(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Design Temperature (°C)</label>
          <input type="number" step="5" min="-30" max="550"
            value={designTemperature} onChange={e=>setDesignTemperature(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Material</label>
          <select value={selectedMaterial} onChange={e=>setSelectedMaterial(e.target.value)}>
            {Object.keys(allowableStressValues).map(k=>(
              <option key={k} value={k}>{materialInfo[k]?.name || k}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label>Joint Efficiency</label>
          <select value={jointEfficiency} onChange={e=>setJointEfficiency(+e.target.value)}>
            <option value="1.0">1.0 — Fully radiographed</option>
            <option value="0.85">0.85 — Spot radiographed</option>
            <option value="0.70">0.70 — No radiography</option>
          </select>
        </div>
        <div className="input-group">
          <label>Corrosion Allowance (mm)</label>
          <input type="number" step="0.5" min="0" max="10"
            value={corrosionAllowance} onChange={e=>setCorrosionAllowance(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Head Type</label>
          <select value={headType} onChange={e=>setHeadType(e.target.value)}>
            <option value="elliptical">2:1 Semi-Elliptical</option>
            <option value="hemispherical">Hemispherical</option>
            <option value="torispherical">Torispherical (Klopper)</option>
          </select>
        </div>
      </div>

      <div className="result-cards">
        <div className="result-card highlight">
          <div className="result-label">Allowable Stress at {designTemperature}°C</div>
          <div className="result-value">{S.toFixed(1)} MPa</div>
          <div className="result-note">{matName}</div>
        </div>
        <div className="result-card">
          <div className="result-label">Shell — Calculated</div>
          <div className="result-value">{tShell.toFixed(2)} mm</div>
          <div className="result-note">Standard: <strong>{stdShell} mm</strong></div>
        </div>
        <div className="result-card">
          <div className="result-label">Head — Calculated</div>
          <div className="result-value">{tHead.toFixed(2)} mm</div>
          <div className="result-note">Standard: <strong>{stdHead} mm</strong></div>
        </div>
      </div>

      <div className="formula-box">
        <strong>Shell:</strong> t = PR / (SE − 0.6P) + CA
        &nbsp;&nbsp;|&nbsp;&nbsp;
        <strong>2:1 Elliptical:</strong> t = PD / (2SE − 0.2P) + CA
        <br/>
        P = {P.toFixed(3)} MPa &nbsp; R = {R.toFixed(0)} mm &nbsp;
        S = {S.toFixed(1)} MPa &nbsp; E = {E} &nbsp; CA = {CA} mm
      </div>

      <div className="info-box">
        <strong>Material Notes:</strong> {materialInfo[selectedMaterial]?.limitations || '—'}<br/>
        <strong>Applications:</strong> {materialInfo[selectedMaterial]?.applications || '—'}
      </div>
    </div>
  );
}

// ─── Vessel Sizing Calculator ────────────────────────────────────────────────

function VesselSizingCalculator() {
  const [vesselType,         setVesselType]         = useState('separator');
  const [orientation,        setOrientation]        = useState('horizontal');
  const [designPressure,     setDesignPressure]     = useState(100);
  const [designTemperature,  setDesignTemperature]  = useState(80);
  const [gasFlowRate,        setGasFlowRate]        = useState(800);   // m³/h actual
  const [liquidFlowRate,     setLiquidFlowRate]     = useState(200);   // m³/h
  const [gasDensity,         setGasDensity]         = useState(30);    // kg/m³
  const [liquidDensity,      setLiquidDensity]      = useState(800);
  const [flowRate,           setFlowRate]           = useState(1000);  // m³/h (non-sep)
  const [residenceTime,      setResidenceTime]      = useState(5);     // min
  const [lOverD,             setLOverD]             = useState(4);
  const [materialCode,       setMaterialCode]       = useState('SA-516-70');
  const [corrosionAllowance, setCorrosionAllowance] = useState(3);
  const [jointEfficiency,    setJointEfficiency]    = useState(0.85);
  const [h2sPresent,         setH2sPresent]         = useState(false);
  const [co2Present,         setCo2Present]         = useState(true);
  const [chloridePresent,    setChloridePresent]    = useState(false);

  // ── Sizing ──────────────────────────────────────────────────────────────
  let diameter = 0, length = 0;

  if (vesselType === 'separator') {
    if (orientation === 'horizontal') {
      // Souders-Brown for gas velocity, liquid residence time for length
      const K_sb = 0.107; // m/s (conservative for horizontal)
      const v_t  = K_sb * Math.sqrt((liquidDensity - gasDensity) / gasDensity);
      // Gas area occupies ~50% of cross-section
      const A_gas  = (gasFlowRate / 3600) / v_t;
      const A_total = A_gas / 0.5;
      diameter = Math.sqrt((4 * A_total) / Math.PI);
      // Check liquid volume
      const V_liq = liquidFlowRate * (residenceTime / 60);
      // Half-full cylinder: L = 8V/(πD²)
      const L_liq = (8 * V_liq) / (Math.PI * diameter ** 2);
      length = Math.max(lOverD * diameter, L_liq);
    } else {
      // Vertical — Souders-Brown for diameter, residence time for height
      const K_sb = 0.0762; // lower K for vertical
      const v_t  = K_sb * Math.sqrt((liquidDensity - gasDensity) / gasDensity);
      const A    = (gasFlowRate / 3600) / v_t;
      diameter   = Math.sqrt((4 * A) / Math.PI);
      const V_liq = liquidFlowRate * (residenceTime / 60);
      // Liquid occupies bottom 75% of height up to HLL
      length = Math.max(lOverD * diameter, (4 * V_liq) / (Math.PI * diameter ** 2) / 0.75);
    }
  } else if (vesselType === 'column') {
    const v_vapor = 0.3; // m/s typical for packed/trayed column
    const A       = (gasFlowRate / 3600) / v_vapor;
    diameter      = Math.sqrt((4 * A) / Math.PI);
    length        = diameter * 25; // typical L/D for columns
  } else {
    // Drum / tank
    const V   = flowRate * (residenceTime / 60);
    diameter  = Math.pow((4 * V) / (Math.PI * lOverD), 1 / 3);
    length    = diameter * lOverD;
  }

  // Round to commercial sizes (50mm D increments, 100mm L)
  const dRounded = Math.ceil(diameter * 1000 / 50) * 50 / 1000;
  const lRounded = Math.ceil(length  * 1000 / 100) * 100 / 1000;
  const volume   = Math.PI * dRounded ** 2 * lRounded / 4;

  // ── Wall thickness ───────────────────────────────────────────────────────
  const S        = getInterpolatedStress(materialCode, designTemperature);
  const P_MPa    = designPressure / 10;
  const R_mm     = (dRounded * 1000) / 2;
  const E        = jointEfficiency;
  const tCalc    = (P_MPa * R_mm) / (S * E - 0.6 * P_MPa) + corrosionAllowance;
  const tStd     = getStandardThickness(tCalc);

  // ── Weight & cost ────────────────────────────────────────────────────────
  const density    = materialDensities[materialCode] || 7850;
  const costFactor = materialCostFactors[materialCode] || 1.0;
  const tM         = tStd / 1000;
  const shellVol   = Math.PI * dRounded * lRounded * tM;
  const headVol    = 2 * (Math.PI / 3) * ((dRounded / 2 + tM) ** 3 - (dRounded / 2) ** 3);
  const totalWeight = (shellVol + headVol) * density * 1.2; // 20% for nozzles/supports
  const estimatedCost = totalWeight * 5 * costFactor; // $5/kg base CS

  // ── Material recommendation ───────────────────────────────────────────────
  const recMat = recommendMaterial(designTemperature, designPressure, h2sPresent, co2Present, chloridePresent);

  return (
    <div className="tool-panel">
      <h3>Vessel Sizing — Separator / Drum / Column</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Preliminary sizing per Souders-Brown (separators) and ASME VIII Div. 1 wall thickness
      </p>

      <div className="two-col-grid">
        <div className="input-group">
          <label>Vessel Type</label>
          <select value={vesselType} onChange={e=>setVesselType(e.target.value)}>
            <option value="separator">Gas-Liquid Separator</option>
            <option value="column">Distillation / Absorption Column</option>
            <option value="drum">Surge Drum / Knockout</option>
            <option value="tank">Storage / Buffer Tank</option>
          </select>
        </div>
        {vesselType === 'separator' && (
          <div className="input-group">
            <label>Orientation</label>
            <select value={orientation} onChange={e=>setOrientation(e.target.value)}>
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
            </select>
          </div>
        )}
        <div className="input-group">
          <label>Design Pressure (barg)</label>
          <input type="number" value={designPressure} onChange={e=>setDesignPressure(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Design Temperature (°C)</label>
          <input type="number" value={designTemperature} onChange={e=>setDesignTemperature(+e.target.value)} />
        </div>

        {vesselType === 'separator' ? (<>
          <div className="input-group">
            <label>Gas Flow Rate (m³/h actual)</label>
            <input type="number" value={gasFlowRate} onChange={e=>setGasFlowRate(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Liquid Flow Rate (m³/h)</label>
            <input type="number" value={liquidFlowRate} onChange={e=>setLiquidFlowRate(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Gas Density (kg/m³)</label>
            <input type="number" value={gasDensity} onChange={e=>setGasDensity(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Liquid Density (kg/m³)</label>
            <input type="number" value={liquidDensity} onChange={e=>setLiquidDensity(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Liquid Residence Time (min)</label>
            <input type="number" value={residenceTime} onChange={e=>setResidenceTime(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Min L/D ratio</label>
            <input type="number" step="0.5" value={lOverD} onChange={e=>setLOverD(+e.target.value)} />
          </div>
        </>) : (<>
          <div className="input-group">
            <label>Flow Rate (m³/h)</label>
            <input type="number" value={flowRate} onChange={e=>setFlowRate(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>Residence Time (min)</label>
            <input type="number" value={residenceTime} onChange={e=>setResidenceTime(+e.target.value)} />
          </div>
          <div className="input-group">
            <label>L/D Ratio</label>
            <input type="number" step="0.5" value={lOverD} onChange={e=>setLOverD(+e.target.value)} />
          </div>
        </>)}
      </div>

      <h4 style={{marginTop:20}}>Material &amp; Service Conditions</h4>
      <div className="two-col-grid">
        <div className="input-group">
          <label>Material</label>
          <select value={materialCode} onChange={e=>setMaterialCode(e.target.value)}>
            {Object.keys(allowableStressValues).map(k=>(
              <option key={k} value={k}>{materialInfo[k]?.name || k}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label>Corrosion Allowance (mm)</label>
          <input type="number" step="0.5" min="0" max="10"
            value={corrosionAllowance} onChange={e=>setCorrosionAllowance(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Joint Efficiency</label>
          <select value={jointEfficiency} onChange={e=>setJointEfficiency(+e.target.value)}>
            <option value="1.0">1.0 — Fully radiographed</option>
            <option value="0.85">0.85 — Spot radiographed</option>
            <option value="0.70">0.70 — No radiography</option>
          </select>
        </div>
        <div className="input-group" style={{paddingTop:8}}>
          <label style={{marginBottom:6}}>Service Flags</label>
          <label style={{fontWeight:'normal', display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
            <input type="checkbox" checked={h2sPresent} onChange={e=>setH2sPresent(e.target.checked)} />
            H₂S Present (Sour Service)
          </label>
          <label style={{fontWeight:'normal', display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
            <input type="checkbox" checked={co2Present} onChange={e=>setCo2Present(e.target.checked)} />
            CO₂ Present
          </label>
          <label style={{fontWeight:'normal', display:'flex', alignItems:'center', gap:8}}>
            <input type="checkbox" checked={chloridePresent} onChange={e=>setChloridePresent(e.target.checked)} />
            Chlorides Present (SCC risk)
          </label>
        </div>
      </div>

      <div className="result-cards" style={{marginTop:20}}>
        <div className="result-card highlight">
          <div className="result-label">Diameter (nominal)</div>
          <div className="result-value">{dRounded.toFixed(3)} m</div>
          <div className="result-note">{(dRounded*1000).toFixed(0)} mm</div>
        </div>
        <div className="result-card highlight">
          <div className="result-label">Length / Height (nominal)</div>
          <div className="result-value">{lRounded.toFixed(2)} m</div>
          <div className="result-note">L/D = {(lRounded/dRounded).toFixed(1)}</div>
        </div>
        <div className="result-card">
          <div className="result-label">Internal Volume</div>
          <div className="result-value">{volume.toFixed(2)} m³</div>
        </div>
        <div className="result-card">
          <div className="result-label">Shell Wall Thickness</div>
          <div className="result-value">{tStd} mm</div>
          <div className="result-note">Calc: {tCalc.toFixed(1)} mm</div>
        </div>
        <div className="result-card">
          <div className="result-label">Total Weight (est.)</div>
          <div className="result-value">{(totalWeight/1000).toFixed(1)} t</div>
        </div>
        <div className="result-card">
          <div className="result-label">Estimated Cost</div>
          <div className="result-value">${Math.round(estimatedCost).toLocaleString()}</div>
          <div className="result-note">USD, supply only</div>
        </div>
      </div>

      {recMat !== materialCode && (
        <div className="info-box warn" style={{marginTop:16}}>
          ⚠️ Based on service conditions, consider <strong>{materialInfo[recMat]?.name || recMat}</strong> instead of selected material.
          &nbsp;{materialInfo[recMat]?.applications}
        </div>
      )}

      <div className="info-box" style={{marginTop:12}}>
        <strong>Sizing basis:</strong> Separators — Souders-Brown terminal velocity (K = {vesselType==='separator' ? (orientation==='horizontal'?'0.107':'0.076') : 'N/A'} m/s),
        liquid retention per residence time. Wall thickness per ASME VIII Div 1 UG-27.
        Cost estimate: base $5/kg CS × material factor ({costFactor.toFixed(1)}×) × 1.2 accessories factor. <em>Preliminary only — not for procurement.</em>
      </div>
    </div>
  );
}

// ─── Top-level VesselTools ───────────────────────────────────────────────────

const VesselTools = () => {
  const [activeTab, setActiveTab] = useState('sizing');
  return (
    <div>
      <div className="sub-tab-nav">
        <button className={`sub-tab-btn${activeTab==='sizing'?' active':''}`}
          onClick={()=>setActiveTab('sizing')}>Vessel Sizing</button>
        <button className={`sub-tab-btn${activeTab==='thickness'?' active':''}`}
          onClick={()=>setActiveTab('thickness')}>Wall Thickness Calculator</button>
      </div>
      {activeTab === 'sizing' ? <VesselSizingCalculator /> : <WallThicknessCalculator />}
    </div>
  );
};

export default VesselTools;
